'use client';

import type { CollaboratorOverheadResponse } from '@/app/models/overhead';
import type { EventsResponseByBadge } from '@/app/models/providersResponse';
import { useListByPeriodAndPersonAndProviderQuery } from '@/app/services/eventsApi';
import formatterMonetaryValue from '@/app/utils/numberUtils';
import formatterPercentualValue from '@/app/utils/percentualUtils';
import { usePeriod } from '@/context/PeriodContext';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import { Box, IconButton, TableCell, TableRow, Typography } from '@mui/material';
import { useState } from 'react';

const mainRowSx = {
  '& > *': { borderBottom: 'unset' },
  backgroundColor: 'grey.100', // Using theme color instead of #f9f9f9
  cursor: 'pointer',
  width: '100%',
};

const mainCellSx = {
  fontSize: 'body2.fontSize', // Using theme typography instead of hardcoded 13
  backgroundColor: 'grey.50', // Using theme color instead of #fafafa
};

const subRowSx = {
  backgroundColor: 'background.paper', // Using theme color instead of 'white'
};

const subCellSx = {
  fontSize: 'body2.fontSize', // Using theme typography instead of hardcoded 13
  backgroundColor: 'background.paper', // Using theme color instead of 'white'
  pl: 5, // theme.spacing(5) for left padding
  pr: 2, // theme.spacing(2) for right padding
  py: 1, // theme.spacing(1) for top/bottom padding
};

const categoryContainerSx = {
  display: 'flex',
};

const categoryTextSx = {
  mr: 7.5, // theme.spacing(7.5) = 60px
  fontSize: 'body2.fontSize',
};

function OverheadCollaboratorTable(props: { row: CollaboratorOverheadResponse }) {
  const { row } = props;
  const { selectedPeriod } = usePeriod();

  const { data } = useListByPeriodAndPersonAndProviderQuery({
    period: selectedPeriod?.date ?? '',
    badge: row.badge,
    code: row.project.code,
  });

  const [open, setOpen] = useState(false);

  return (
    <>
      <TableRow sx={mainRowSx} onClick={() => setOpen(!open)}>
        <TableCell sx={mainCellSx}>
          <IconButton
            aria-label="expand row"
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              setOpen(!open);
            }}
          >
            {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
          </IconButton>
          {`${row.badge} - ${row.person.name}`}
        </TableCell>
        <TableCell sx={mainCellSx}>{`${formatterMonetaryValue(row.salaryValue)}`}</TableCell>
        <TableCell sx={mainCellSx}>{`${formatterMonetaryValue(row.salaryToConsider)}`}</TableCell>
        <TableCell sx={mainCellSx}>{`${formatterMonetaryValue(row.valueToBePaid)}`}</TableCell>
        <TableCell sx={mainCellSx} align="center">
          {`${formatterPercentualValue(row.percent)}`}
        </TableCell>
        <TableCell sx={mainCellSx} align="center">
          {`${formatterPercentualValue(row.balancePercent)}`}
        </TableCell>
        <TableCell sx={mainCellSx} align="center">
          {`${formatterPercentualValue(row.overheadPercent)}`}
        </TableCell>
      </TableRow>

      {open &&
        data?.map((subRow: EventsResponseByBadge) => (
          <TableRow key={subRow.badge + subRow.value + subRow.code} sx={subRowSx}>
            <TableCell component="th" scope="row" sx={subCellSx}>
              <Box sx={categoryContainerSx}>
                <Typography
                  sx={categoryTextSx}
                >{`${subRow.expenseGroup} - ${subRow.expenseCode} - ${subRow.expenseName}`}</Typography>
              </Box>
            </TableCell>
            <TableCell align="left" sx={{ ...subCellSx, pl: 2 }}>
              {`${formatterMonetaryValue(subRow.value)}`}
            </TableCell>
            <TableCell sx={{ ...subCellSx, pl: 2 }} />
            <TableCell sx={{ ...subCellSx, pl: 2 }} />
            <TableCell sx={{ ...subCellSx, pl: 2 }} />
            <TableCell sx={{ ...subCellSx, pl: 2 }} />
          </TableRow>
        ))}
    </>
  );
}

export default OverheadCollaboratorTable;
